import { useState, useRef, useCallback } from "react";
import { supabase } from "@/lib/supabase";

interface ImageUploaderProps {
  value: string;
  onChange: (url: string) => void;
  placeholder?: string;
}

export default function ImageUploader({ value, onChange, placeholder }: ImageUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleUpload = useCallback(async (file: File) => {
    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file (JPG, PNG, WebP, etc.)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be under 5MB");
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const fileExt = file.name.split(".").pop() || "jpg";
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 10)}.${fileExt}`;
      const filePath = `uploads/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("images")
        .upload(filePath, file, { cacheControl: "3600", upsert: false });

      if (uploadError) {
        // If bucket doesn't exist, fall back to URL input
        if (uploadError.message?.includes("does not exist") || uploadError.message?.includes("not found")) {
          setError("Storage not configured. Please paste an image URL instead.");
          setUploading(false);
          return;
        }
        throw uploadError;
      }

      const { data: urlData } = supabase.storage.from("images").getPublicUrl(filePath);
      if (urlData?.publicUrl) {
        onChange(urlData.publicUrl);
      }
    } catch (err: any) {
      console.error("Upload error:", err);
      setError(err.message || "Upload failed. Try pasting a URL instead.");
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  }, [handleUpload]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    if (inputRef.current) inputRef.current.value = "";
  }, [handleUpload]);

  return (
    <div className="flex flex-col gap-2">
      {/* URL input */}
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setError(null);
        }}
        placeholder={placeholder || "https://... or upload below"}
        className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:outline-none focus:border-primary text-stone-800 placeholder-stone-400"
      />

      {/* Dropzone */}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200 ${
          isDragging
            ? "border-primary bg-primary/5"
            : "border-stone-200 hover:border-stone-300 bg-stone-50/50"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />

        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <i className="ri-loader-4-line animate-spin text-primary text-xl"></i>
            <span className="text-sm text-stone-500">Uploading image...</span>
          </div>
        ) : value ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-32 h-20 rounded-lg overflow-hidden border border-stone-200">
              <img src={value} alt="Preview" className="w-full h-full object-cover" />
            </div>
            <span className="text-xs text-stone-400">Click or drag to replace</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center text-stone-300">
              <i className="ri-upload-cloud-line text-2xl"></i>
            </div>
            <p className="text-sm text-stone-500 font-medium">
              Drag & drop an image here
            </p>
            <p className="text-xs text-stone-400">
              or click to browse — JPG, PNG, WebP (max 5MB)
            </p>
          </div>
        )}
      </div>

      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <i className="ri-error-warning-line"></i>
          {error}
        </p>
      )}
    </div>
  );
}